import { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Typography,
} from '@mui/material';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { useUpdateProductMutation } from '@/apis/extended/productApi';
import { formatPrice } from '@/utils/helper';
import { Product } from '@/types/ProductType';

type PrepaymentPenaltyModalProps = {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  estimation: any;
  onUpdate: () => void;
};

export const PrepaymentPenaltyModal = ({
  isOpen,
  onClose,
  product,
  estimation,
  onUpdate,
}: PrepaymentPenaltyModalProps) => {
  const [updateProduct, { isLoading: isUpdatingProduct }] =
    useUpdateProductMutation();
  const [removePrepaymentPenalty, setRemovePrepaymentPenalty] = useState(
    product?.loanInfo?.removePrepaymentPenalty || false
  );

  const handleSave = () => {
    updateProduct({
      id: product._id,
      removePrepaymentPenalty,
    }).then(() => {
      onUpdate();
      onClose();
    });
  };

  if (estimation.type !== USER_LOAN_PROGRAM.DSCR) {
    return null;
  }

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Prepayment Penalty Details</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          This DSCR loan includes a prepayment penalty schedule. If you pay off
          the loan early, you'll be charged a percentage of the remaining
          balance.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Prepayment Penalty Schedule:
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Year 1: {formatPrice(estimation.prepaymentPenaltyYear1)} (
            {(
              (estimation.prepaymentPenaltyYear1 / estimation.loanAmount) *
              100
            ).toFixed(1)}
            %)
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Year 2: {formatPrice(estimation.prepaymentPenaltyYear2)} (
            {(
              (estimation.prepaymentPenaltyYear2 / estimation.loanAmount) *
              100
            ).toFixed(1)}
            %)
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Year 3: {formatPrice(estimation.prepaymentPenaltyYear3)} (
            {(
              (estimation.prepaymentPenaltyYear3 / estimation.loanAmount) *
              100
            ).toFixed(1)}
            %)
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Year 4: {formatPrice(estimation.prepaymentPenaltyYear4)} (
            {(
              (estimation.prepaymentPenaltyYear4 / estimation.loanAmount) *
              100
            ).toFixed(1)}
            %)
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Year 5: {formatPrice(estimation.prepaymentPenaltyYear5)} (
            {(
              (estimation.prepaymentPenaltyYear5 / estimation.loanAmount) *
              100
            ).toFixed(1)}
            %)
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={removePrepaymentPenalty}
                onChange={(e) => setRemovePrepaymentPenalty(e.target.checked)}
              />
            }
            label="Remove prepayment penalty"
          />
          <Typography variant="body2" sx={{ ml: 4, color: 'text.primary' }}>
            Removing the prepayment penalty will increase your interest rate by
            0.5% and add a 1% additional lender origination fee of{' '}
            {formatPrice(estimation.loanAmount * 0.01)}.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>Cancel</Button>
          <LoadingButton
            variant="contained"
            onClick={handleSave}
            loading={isUpdatingProduct}
          >
            Save Changes
          </LoadingButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
